import trackSelectedHomeDeliveryAddress from '@helpers/analyticsHelpers/trackSelectedHomeDeliveryAddress';
import logger from '@logger';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import useCustomer from '@hooks/useCustomer';
import checkoutHelper from '@helpers/checkoutHelper';
import {
  CartAddressType,
  deleteSlotInCart,
  setAddressOnCart,
  setDeliveryMode,
  setPostalCodeOnCart,
} from '@api/interfaces/cartApi';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useAbortController from '@hooks/useAbortController';
import { useMemo } from 'react';
import paths from '@constants/paths';
import { usePathname } from 'next/navigation';

interface Props {
  afterSubmit?: () => void;
}

const useFinishSubmission = ({ afterSubmit }: Props) => {
  const { customer } = useCustomer();
  const checkout = checkoutHelper();
  const { refreshCart } = useCart();
  const checkCartConflict = useCheckCartConflict();
  const { store, refreshStore } = useStore();
  const { getSignal } = useAbortController();
  const pathname = usePathname();
  const isCheckoutPath = useMemo(() => pathname === paths.CHECKOUT, [pathname]);

  const handleStoreSwitch = async (deliveryStoreId: string) => {
    const { activateStore } = await import('@api/interfaces/storeApi');
    const response = await activateStore(deliveryStoreId, true, true, getSignal());

    await refreshStore(response.data);
    await deleteSlotInCart();
  };

  return async (
    deliveryStoreId: string,
    addressParams: AddressParamsType,
    prefetchedAddressData: CustomerCoordinatesType,
    callback?: () => void
  ) => {
    try {
      await checkCartConflict({ storeId: deliveryStoreId });
      await setDeliveryMode('homeDelivery', '');

      if (store?.storeId !== deliveryStoreId || !store?.activelySelected) {
        await handleStoreSwitch(deliveryStoreId);
      }

      const setAddressParams: CartAddressType = {
        addressLine: addressParams.addressLine || '',
        addressLine2: addressParams.addressLine2 || customer?.defaultShippingAddress?.line2 || '',
        postalCode: addressParams.postalCode || '',
        locality: addressParams.locality || '',
        longitude: prefetchedAddressData?.longitude || undefined,
        latitude: prefetchedAddressData?.latitude || undefined,
        firstName:
          prefetchedAddressData?.firstName || customer?.defaultShippingAddress?.firstName || checkout?.firstName || '',
        lastName:
          prefetchedAddressData?.lastName || customer?.defaultShippingAddress?.lastName || checkout?.lastName || '',
        cellphone: customer?.defaultShippingAddress?.cellphone || checkout?.cellphone || '',
        email: customer?.email || checkout?.email || '',
        companyName:
          addressParams?.companyName ||
          customer?.companyAlternativeName ||
          customer?.defaultShippingAddress?.companyName ||
          undefined,
      };

      await setAddressOnCart(setAddressParams);
      if (addressParams.postalCode) {
        await setPostalCodeOnCart(addressParams.postalCode);
      }
      const cartData = await refreshCart();
      await refreshCart(cartData);

      trackSelectedHomeDeliveryAddress({ deliveryStoreId, isCheckoutPath });

      if (afterSubmit) afterSubmit();
    } catch (err: any) {
      logger.error({ error: 'Could not finish home delivery submit', message: err.message });
      if (callback) callback();
    }
  };
};

export default useFinishSubmission;
